import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, RefreshCw, LogOut, Pencil, Mail, Sparkles, Link2, Send } from "lucide-react";
import { toast } from "sonner";

const EMPTY_JOB = { title: "", organization: "", department: "ssc", state: "All India", qualification: "Graduate", job_type: "permanent", total_posts: 0, salary_min: 0, salary_max: 0, age_min: 18, age_max: 35, last_date: "", notification_date: "", apply_link: "", notification_link: "", description: "", eligibility: "", how_to_apply: "", important_dates: "", application_fee: "", selection_process: "", is_featured: false };
const EMPTY_AC = { title: "", organization: "", exam_name: "", exam_date: "", release_date: "", download_link: "", description: "" };
const EMPTY_RES = { title: "", organization: "", exam_name: "", result_date: "", result_link: "", cutoff_link: "", description: "" };
const EMPTY_ARTICLE = { title: "", excerpt: "", category: "jobs-guide", tags: [], content: "", author: "Dileshwar Vitthale" };

function buildWhatsAppMessage(job) {
  const site = "https://hireds.in";
  const link = `${site}/jobs/${job.slug}`;
  const lines = [
    `🚨 *${job.title}*`,
    job.organization ? `🏛 ${job.organization}` : null,
    job.total_posts ? `👥 Total vacancies: *${job.total_posts}*` : null,
    job.qualification ? `🎓 Qualification: ${job.qualification}` : null,
    job.last_date ? `🗓 Last date: *${(job.last_date || "").slice(0, 10)}*` : null,
    (job.salary_min || job.salary_max) ? `💰 Salary: ₹${job.salary_min || 0} – ₹${job.salary_max || job.salary_min || 0}/month` : null,
    "",
    `📲 Apply / full details: ${link}`,
    "",
    `🔔 Get instant alerts: ${site}`,
    `📢 Channel: hireds.in — Latest Sarkari Naukri`,
  ];
  return lines.filter(Boolean).join("\n");
}

async function copyJobToWhatsApp(job) {
  const msg = buildWhatsAppMessage(job);
  try {
    await navigator.clipboard.writeText(msg);
    toast.success("Copied! Open your WhatsApp channel and paste (Ctrl+V / long-press → Paste).", { duration: 6000 });
    // Also open WhatsApp Web with prefilled text — user picks the channel
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  } catch (e) {
    toast.error("Could not copy — please copy the message manually from the prompt.");
    window.prompt("Copy this message to your WhatsApp channel:", msg);
  }
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [admits, setAdmits] = useState([]);
  const [results, setResults] = useState([]);
  const [articles, setArticles] = useState([]);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    const [j, a, r, ar, s, m] = await Promise.all([
      api.get("/jobs?limit=100"),
      api.get("/admit-cards?limit=100"),
      api.get("/results?limit=100"),
      api.get("/admin/articles").catch(()=>({data:{items:[]}})),
      api.get("/admin/newsletter/subscribers").catch(()=>({data:{items:[]}})),
      api.get("/admin/contact-messages").catch(()=>({data:{items:[]}})),
    ]);
    setJobs(j.data.items); setAdmits(a.data.items); setResults(r.data.items);
    setArticles(ar.data.items); setSubs(s.data.items); setMsgs(m.data.items);
  };

  useEffect(()=>{ if (user && user.role==="admin") refresh(); }, [user]);

  const triggerAuto = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/auto-update");
      toast.success(`Added ${data.jobs_added} jobs, ${data.admits_added} admits, ${data.results_added} results`);
      await refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };

  const sendDigest = async (frequency) => {
    if (!window.confirm(`Send ${frequency} digest to all ${frequency} subscribers now?`)) return;
    try {
      const { data } = await api.post(`/admin/send-digest?frequency=${frequency}`, {});
      toast.success(`Sent ${data.sent} of ${data.subscribers} ${frequency} digests`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const enrichBatch = async () => {
    if (!window.confirm("Enrich up to 10 un-enriched jobs with AI? This runs in background and takes 2-5 minutes.")) return;
    try {
      const { data } = await api.post("/admin/jobs/enrich-batch?limit=10");
      toast.success(data.message || "Enrichment started");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const introBatch = async () => {
    if (!window.confirm("Generate the 100-word AI intro for ALL jobs missing one (Claude Haiku 4.5)? Runs in background, ~2s per job. Cheap and cached forever.")) return;
    try {
      const { data } = await api.post("/admin/jobs/intro-batch?limit=500");
      toast.success(data.message || "Intro batch started");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const sendPushBroadcast = async () => {
    const title = window.prompt("Push notification TITLE (e.g., '🚨 SSC CGL 2026 Notification Out')", "");
    if (!title) return;
    const body = window.prompt("Push notification BODY (one short sentence — what's the news?)", "");
    if (!body) return;
    const url = window.prompt("Deep-link URL (e.g., /jobs/ssc-cgl-2026 or /latest-jobs)", "/latest-jobs");
    const department = window.prompt("Filter by sector (railway / ssc / banking / upsc / defense / teaching / state-govt / psu) — leave blank for ALL subscribers", "");
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title, body, url: url || "/latest-jobs",
        department: department || null,
      });
      toast.success(data.message || "Push queued");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const indexnowSyncAll = async () => {
    if (!window.confirm("Submit EVERY URL in the sitemap to IndexNow (Bing/Yandex/Seznam) for instant re-crawl? This is safe — Google ignores IndexNow, Bing does not penalise. Best run right after a redeploy.")) return;
    try {
      const { data } = await api.post("/admin/indexnow/sync-all");
      toast.success(data.message || "IndexNow sync queued");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const validateLinks = async () => {
    try {
      const { data } = await api.post("/admin/jobs/validate-links");
      toast.success(data.message || "Validation started");
      setTimeout(async () => {
        const { data: bd } = await api.get("/admin/jobs/broken-links");
        if (bd.total > 0) {
          toast.warning(`Found ${bd.total} broken link(s). Check 'Broken Links' filter in Jobs tab.`);
        } else {
          toast.success("All job apply links look healthy");
        }
      }, 45000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const enrichOne = async (jobId) => {
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/enrich`);
      toast.success(data.message || "Enrichment started");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace/>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Admin Panel</div>
          <h1 className="text-3xl font-extrabold" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Dashboard</h1>
          <div className="text-sm text-slate-500 mt-1">{user.email}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="auto-update-btn" onClick={triggerAuto} disabled={running} className="bg-orange-600 hover:bg-orange-700 text-white"><RefreshCw className={`w-4 h-4 mr-2 ${running?'animate-spin':''}`}/>{running?"Running…":"Run Daily Auto-Update"}</Button>
          <Button data-testid="enrich-batch-btn" onClick={enrichBatch} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"><Sparkles className="w-4 h-4 mr-2"/>Enrich Jobs (AI)</Button>
          <Button data-testid="intro-batch-btn" onClick={introBatch} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50"><Sparkles className="w-4 h-4 mr-2"/>Generate AI Intros (Bulk)</Button>
          <Button data-testid="push-broadcast-btn" onClick={sendPushBroadcast} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"><Sparkles className="w-4 h-4 mr-2"/>Send Push Broadcast</Button>
          <Button data-testid="indexnow-sync-btn" onClick={indexnowSyncAll} variant="outline" className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"><Sparkles className="w-4 h-4 mr-2"/>Sync to IndexNow</Button>
          <Link to="/admin/seo">
            <Button data-testid="seo-dashboard-link" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"><Sparkles className="w-4 h-4 mr-2"/>SEO Dashboard</Button>
          </Link>
          <Button data-testid="validate-links-btn" onClick={validateLinks} variant="outline" className="border-slate-300"><Link2 className="w-4 h-4 mr-2"/>Validate Links</Button>
          <Button data-testid="send-daily-digest-btn" onClick={()=>sendDigest("daily")} variant="outline" className="border-slate-300"><Mail className="w-4 h-4 mr-2"/>Send Daily Digest</Button>
          <Button data-testid="send-weekly-digest-btn" onClick={()=>sendDigest("weekly")} variant="outline" className="border-slate-300"><Mail className="w-4 h-4 mr-2"/>Send Weekly Digest</Button>
          <Button data-testid="logout-btn" variant="outline" onClick={logout}><LogOut className="w-4 h-4 mr-2"/>Logout</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[
          {label:"Jobs", val:jobs.length},
          {label:"Admit Cards", val:admits.length},
          {label:"Results", val:results.length},
          {label:"Articles", val:articles.length},
          {label:"Subscribers", val:subs.length},
          {label:"Messages", val:msgs.length},
        ].map((s)=>(
          <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{s.label}</div>
            <div className="text-2xl font-extrabold mt-1">{s.val}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="jobs">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
          <TabsTrigger value="admits" data-testid="tab-admits">Admit Cards</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
          <TabsTrigger value="articles" data-testid="tab-articles">Articles / Blog</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs">Subscribers</TabsTrigger>
          <TabsTrigger value="msgs" data-testid="tab-msgs">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs">
          <CrudList kind="jobs" items={jobs} empty={EMPTY_JOB} fields={JOB_FIELDS} refresh={refresh} enrichOne={enrichOne}/>
        </TabsContent>
        <TabsContent value="admits">
          <CrudList kind="admit-cards" items={admits} empty={EMPTY_AC} fields={AC_FIELDS} refresh={refresh}/>
        </TabsContent>
        <TabsContent value="results">
          <CrudList kind="results" items={results} empty={EMPTY_RES} fields={RES_FIELDS} refresh={refresh}/>
        </TabsContent>
        <TabsContent value="articles">
          <CrudList kind="articles" items={articles} empty={EMPTY_ARTICLE} fields={ARTICLE_FIELDS} refresh={refresh} fetchFull/>
        </TabsContent>
        <TabsContent value="subs">
          <SimpleList rows={subs} cols={[["email","Email"],["frequency","Freq"],["subscribed_at","Date","date"]]}/>
        </TabsContent>
        <TabsContent value="msgs">
          <SimpleList rows={msgs} cols={[["name","Name"],["email","Email"],["subject","Subject"],["message","Message"],["received_at","Date","date"]]}/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const JOB_FIELDS = [
  { k:"title", label:"Title", req:true, full:true },
  { k:"organization", label:"Organization", req:true },
  { k:"department", label:"Department", type:"select", opts:["railway","banking","ssc","upsc","defense","teaching","state-govt","psu"] },
  { k:"state", label:"State" }, { k:"qualification", label:"Qualification" },
  { k:"job_type", label:"Job Type", type:"select", opts:["permanent","contract","apprentice","internship"] },
  { k:"total_posts", label:"Total Posts", type:"number" },
  { k:"salary_min", label:"Salary Min", type:"number" }, { k:"salary_max", label:"Salary Max", type:"number" },
  { k:"age_min", label:"Age Min", type:"number" }, { k:"age_max", label:"Age Max", type:"number" },
  { k:"last_date", label:"Last Date (YYYY-MM-DD)" }, { k:"notification_date", label:"Notification Date" },
  { k:"apply_link", label:"Apply Link", full:true }, { k:"notification_link", label:"Notification Link", full:true },
  { k:"description", label:"Description", type:"textarea", full:true },
  { k:"eligibility", label:"Eligibility", type:"textarea", full:true },
  { k:"how_to_apply", label:"How to Apply", type:"textarea", full:true },
  { k:"important_dates", label:"Important Dates", type:"textarea", full:true },
  { k:"application_fee", label:"Application Fee" }, { k:"selection_process", label:"Selection Process" },
];
const AC_FIELDS = [
  { k:"title", label:"Title", req:true, full:true }, { k:"organization", label:"Organization", req:true },
  { k:"exam_name", label:"Exam Name" }, { k:"exam_date", label:"Exam Date" },
  { k:"release_date", label:"Release Date" }, { k:"download_link", label:"Download Link", full:true },
  { k:"description", label:"Description", type:"textarea", full:true },
];
const RES_FIELDS = [
  { k:"title", label:"Title", req:true, full:true }, { k:"organization", label:"Organization", req:true },
  { k:"exam_name", label:"Exam Name" }, { k:"result_date", label:"Result Date" },
  { k:"result_link", label:"Result Link", full:true }, { k:"cutoff_link", label:"Cutoff Link", full:true },
  { k:"description", label:"Description", type:"textarea", full:true },
];
const ARTICLE_FIELDS = [
  { k:"title", label:"Title", req:true, full:true },
  { k:"category", label:"Category", type:"select", opts:["jobs-guide","exam-prep","current-affairs","news","education","online-certification","career-guides","interview-prep","industry-news","finance-insurance","masters-degrees","salary-negotiation","remote-work","recruitment-process","employee-benefits","workplace-insurance","resume-portfolio"] },
  { k:"author", label:"Author" },
  { k:"excerpt", label:"Excerpt (1-2 sentence summary)", type:"textarea", full:true },
  { k:"content", label:"Content (markdown: use # H1, ## H2, ### H3, **bold**, - lists)", type:"textarea-large", full:true },
];

function CrudList({ kind, items, empty, fields, refresh, fetchFull, enrichOne }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const startCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const startEdit = async (it) => {
    let full = it;
    if (fetchFull && kind === "articles" && it.slug) {
      try { const { data } = await api.get(`/articles/${it.slug}`); full = data; } catch (e) {}
    }
    setEdit(it); setForm({...empty, ...full}); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {};
      Object.keys(empty).forEach(k => payload[k] = form[k] !== undefined ? form[k] : empty[k]);
      ["total_posts","salary_min","salary_max","age_min","age_max"].forEach(k=>{ if (k in payload) payload[k] = Number(payload[k])||0; });
      if (edit) await api.put(`/admin/${kind}/${edit.id}`, payload);
      else await api.post(`/admin/${kind}`, payload);
      toast.success(edit?"Updated":"Created"); setOpen(false); await refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/admin/${kind}/${id}`); toast.success("Deleted"); await refresh(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-500">{items.length} entries</div>
        <Button data-testid={`add-${kind}-btn`} onClick={startCreate} className="bg-slate-900 text-white hover:bg-slate-800"><Plus className="w-4 h-4 mr-1"/>Add</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.map((it, i)=>(
          <div key={it.id} data-testid={`${kind}-item-${i}`} className="p-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                {it.title}
                {kind === "jobs" && it.enriched && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800">AI ✓</span>}
                {kind === "jobs" && it.apply_link_status === "broken" && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800">BROKEN LINK</span>}
              </div>
              <div className="text-xs text-slate-500">{it.organization} · {fmtDate(it.posted_date)}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              {kind === "jobs" && (
                <Button size="sm" variant="outline" onClick={()=>copyJobToWhatsApp(it)} title="Copy to WhatsApp channel" className="border-green-300 text-green-700 hover:bg-green-50" data-testid={`wa-share-${i}`}><Send className="w-3.5 h-3.5"/></Button>
              )}
              {kind === "jobs" && enrichOne && !it.enriched && (
                <Button size="sm" variant="outline" onClick={()=>enrichOne(it.id)} title="Enrich with AI" className="border-orange-300 text-orange-700 hover:bg-orange-50"><Sparkles className="w-3.5 h-3.5"/></Button>
              )}
              <Button size="sm" variant="outline" onClick={()=>startEdit(it)} title="Edit"><Pencil className="w-3.5 h-3.5"/></Button>
              <Button size="sm" variant="outline" onClick={()=>del(it.id)} className="text-red-600 border-red-200 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5"/></Button>
            </div>
          </div>
        ))}
        {items.length===0 && <div className="p-12 text-center text-slate-500 text-sm">No entries.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?"Edit":"Add new"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {fields.map((f)=>(
              <div key={f.k} className={f.full?"md:col-span-2":""}>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700">{f.label}</label>
                {f.type==="textarea" ? (
                  <Textarea rows={3} value={form[f.k]||""} onChange={(e)=>setForm({...form,[f.k]:e.target.value})} className="mt-1"/>
                ) : f.type==="textarea-large" ? (
                  <Textarea rows={20} value={form[f.k]||""} onChange={(e)=>setForm({...form,[f.k]:e.target.value})} className="mt-1 font-mono text-sm"/>
                ) : f.type==="select" ? (
                  <Select value={form[f.k]||f.opts[0]} onValueChange={(v)=>setForm({...form,[f.k]:v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{f.opts.map(o=>(<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type==="number"?"number":"text"} value={form[f.k]??""} onChange={(e)=>setForm({...form,[f.k]:e.target.value})} className="mt-1"/>
                )}
              </div>
            ))}
            <div className="md:col-span-2 flex items-center gap-2">
              <input id="featured" type="checkbox" checked={!!form.is_featured} onChange={(e)=>setForm({...form,is_featured:e.target.checked})}/>
              <label htmlFor="featured" className="text-sm">Featured</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button data-testid="save-entry-btn" onClick={save} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SimpleList({ rows, cols }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid p-3 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600" style={{gridTemplateColumns:`repeat(${cols.length}, 1fr)`}}>
        {cols.map(c=>(<div key={c[0]}>{c[1]}</div>))}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r,i)=>(
          <div key={i} className="grid p-3 text-sm" style={{gridTemplateColumns:`repeat(${cols.length}, 1fr)`}}>
            {cols.map(c=>(<div key={c[0]} className="truncate pr-2">{c[2]==='date'?fmtDate(r[c[0]]):(r[c[0]]||"—")}</div>))}
          </div>
        ))}
        {rows.length===0 && <div className="p-12 text-center text-slate-500 text-sm">No entries.</div>}
      </div>
    </div>
  );
}
